import React, { useState, useEffect } from 'react';
import {
	ClockIcon,
	EyeIcon,
	ArrowsRightLeftIcon,
	CheckCircleIcon,
	XCircleIcon,
} from '@heroicons/react/24/outline';
import { getSopVersions, getSopVersion } from '../../api';
import { classNames } from '../helpers/functions';

const STATUS_CONFIG = {
	draft: {
		label: 'Draft',
		color: 'bg-yellow-100 text-yellow-800',
		icon: ClockIcon,
	},
	approved: {
		label: 'Approved',
		color: 'bg-green-100 text-green-800',
		icon: CheckCircleIcon,
	},
	deprecated: {
		label: 'Deprecated',
		color: 'bg-red-100 text-red-800',
		icon: XCircleIcon,
	},
};

const formatDate = (date) => {
	if (!date) return 'N/A';
	return new Date(date).toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

// Helper to format version string
const formatVersion = (major, minor) => {
	return `${major || 0}.${minor || 0}`;
};

// Helper to get version key for selection
const getVersionKey = (version) => {
	return `${version.versionMajor}.${version.versionMinor}`;
};

/**
 * SopVersionHistory - Displays version history for an SOP
 *
 * @param {Object} props
 * @param {string} props.sopId - The SOP ID
 * @param {Function} props.onViewVersion - Callback when viewing a version
 * @param {Function} props.onCompareVersions - Callback when comparing versions
 */
const SopVersionHistory = ({ sopId, onViewVersion, onCompareVersions }) => {
	const [versions, setVersions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selectedVersions, setSelectedVersions] = useState([]); // Array of version strings "major.minor"

	useEffect(() => {
		const fetchVersions = async () => {
			if (!sopId) return;

			setLoading(true);
			setError(null);

			try {
				const data = await getSopVersions({ id: sopId });
				setVersions(data || []);
			} catch (err) {
				console.error('Failed to fetch versions:', err);
				setError('Failed to load version history');
			} finally {
				setLoading(false);
			}
		};

		fetchVersions();
	}, [sopId]);

	const handleVersionSelect = (versionKey) => {
		if (selectedVersions.includes(versionKey)) {
			setSelectedVersions(selectedVersions.filter((v) => v !== versionKey));
		} else if (selectedVersions.length < 2) {
			setSelectedVersions([...selectedVersions, versionKey]);
		} else {
			// Replace oldest selection
			setSelectedVersions([selectedVersions[1], versionKey]);
		}
	};

	const handleCompare = () => {
		if (selectedVersions.length === 2 && onCompareVersions) {
			// Sort versions to pass older first
			const sorted = [...selectedVersions].sort((a, b) => {
				const [aMaj, aMin] = a.split('.').map(Number);
				const [bMaj, bMin] = b.split('.').map(Number);
				if (aMaj !== bMaj) return aMaj - bMaj;
				return aMin - bMin;
			});
			onCompareVersions(sorted[0], sorted[1]);
		}
	};

	const handleView = async (version) => {
		if (onViewVersion) {
			try {
				const versionStr = formatVersion(
					version.versionMajor,
					version.versionMinor,
				);
				const versionData = await getSopVersion({
					id: sopId,
					version: versionStr,
				});
				onViewVersion(versionData);
			} catch (err) {
				console.error('Failed to fetch version:', err);
			}
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center py-8">
				<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
				<span className="ml-2 text-sm text-gray-500">Loading versions...</span>
			</div>
		);
	}

	if (error) {
		return (
			<div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
				{error}
			</div>
		);
	}

	if (versions.length === 0) {
		return (
			<div className="text-center py-8">
				<ClockIcon className="mx-auto h-12 w-12 text-gray-300" />
				<h3 className="mt-2 text-sm font-medium text-gray-900">
					No versions yet
				</h3>
				<p className="mt-1 text-sm text-gray-500">
					Publish the SOP to create the first version.
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Compare button */}
			{selectedVersions.length === 2 && (
				<div className="flex justify-end">
					<button
						type="button"
						onClick={handleCompare}
						className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
					>
						<ArrowsRightLeftIcon className="h-4 w-4" />
						Compare v{selectedVersions[0]} and v{selectedVersions[1]}
					</button>
				</div>
			)}

			{/* Selection hint */}
			{selectedVersions.length === 1 && (
				<p className="text-sm text-gray-500 text-right">
					Select one more version to compare
				</p>
			)}

			{/* Version list */}
			<div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
				{versions.map((version, vi) => {
					const StatusIcon = STATUS_CONFIG[version.status]?.icon || ClockIcon;
					const versionKey = getVersionKey(version);
					const isSelected = selectedVersions.includes(versionKey);
					const versionStr = formatVersion(
						version.versionMajor,
						version.versionMinor,
					);

					console.log('version: ', version);

					return (
						<div
							key={versionKey}
							className={classNames(
								'flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition-colors',
								isSelected ? 'bg-indigo-50' : '',
							)}
							onClick={() => handleVersionSelect(versionKey)}
						>
							<div className="flex items-center gap-4">
								{/* Selection checkbox */}
								<div
									className={classNames(
										'flex h-5 w-5 items-center justify-center rounded border-2 transition-colors',
										isSelected
											? 'border-indigo-600 bg-indigo-600 text-white'
											: 'border-gray-300',
									)}
								>
									{isSelected && (
										<svg
											className="h-3 w-3"
											fill="currentColor"
											viewBox="0 0 12 12"
										>
											<path d="M3.707 5.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L5 6.586 3.707 5.293z" />
										</svg>
									)}
								</div>

								{/* Version info */}
								<div>
									<div className="flex items-center gap-2">
										<span className="font-medium text-gray-900 font-mono">
											v{versionStr}
										</span>
										{vi === 0 ? (
											<span
												className={classNames(
													'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
													STATUS_CONFIG[version.status]?.color ||
														'bg-gray-100 text-gray-800',
												)}
											>
												Latest
											</span>
										) : null}
									</div>
									<div className="mt-1 text-sm text-gray-500">
										<span>{version.name}</span>
									</div>
									<div className="mt-1 text-xs text-gray-400">
										Published {formatDate(version.publishedAt)}
										{version.publishedByUser && (
											<span>
												{' '}
												by {version.publishedByUser.firstName}{' '}
												{version.publishedByUser.lastName}
											</span>
										)}
									</div>
								</div>
							</div>

							{/* View button */}
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									handleView(version);
								}}
								className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
							>
								<EyeIcon className="h-4 w-4" />
								View
							</button>
						</div>
					);
				})}
			</div>

			{/* Instructions */}
			<p className="text-xs text-gray-500 text-center">
				Click on versions to select them for comparison
			</p>
		</div>
	);
};

export default SopVersionHistory;
